import { Button, Stack, Typography } from '@mui/material';
import {
  FC,
  MouseEvent,
  useEffect,
  useMemo,
  useState,
  Dispatch,
  SetStateAction,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import { orderDetailsState } from '../../../states/orderDetails/orderDetails.state.ts';
import { snackbarService } from '../../../../../../shared/components/snackbar/service/snackbar.service';
import { WarningsOnCreationPopup } from '../../../../../../shared/components/popupTemplates/warningsOnCreation.popup';
import {
  C_Order_Error_Code,
  Order_Offer_Warning_Types,
  Order_Offer_Warning_Type_Fields,
} from '../../../../../../graphql/generatedModel';
import { IOrderCreation } from '../../../../create/states/orderDetails/orderCreation.state.ts';
import { Table } from '../../../../../../shared/components/table/table.component.tsx';
import { GridColDef } from '@mui/x-data-grid-pro';
import { Column } from '../../../../../../shared/components/table/components/common.components.tsx';

export const OrderActions: FC<IOrderActions> = ({ dirty, setIsEmptyTourError }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const confirmCreationModal = useMemo(() => new WarningsOnCreationPopup(), []);

  const generateWarningTable = useCallback(
    (linkedRecords: IOrderCreation['linkedRecords']) => {
      if (linkedRecords) {
        const linkedRecordsArr = Object.keys(linkedRecords);
        const warningKey = linkedRecordsArr[0] as keyof typeof Order_Offer_Warning_Types;
        let enabledFields: Array<string> = [];
        const warningValues = linkedRecords[warningKey];
        if (warningKey && warningValues) {
          enabledFields = Object.keys(warningValues[0]);
          if (enabledFields.length) {
            return (
              <Table data={warningValues.map((el, i) => ({ ...el, id: i }))} height={210}>
                {enabledFields.map((field, idx) => {
                  const props: GridColDef = {
                    field,
                  };
                  switch (field) {
                    case Order_Offer_Warning_Type_Fields.ARTICLE:
                      props.flex = 1;
                      break;
                    case Order_Offer_Warning_Type_Fields.DAYS_TO_PRODUCE:
                      props.align = 'right';
                      props.headerAlign = 'right';
                      props.width = 180;
                      break;
                    case Order_Offer_Warning_Type_Fields.QUANTITY_PER_LOT:
                      props.align = 'right';
                      props.headerAlign = 'right';
                      props.width = 150;
                      break;
                    case Order_Offer_Warning_Type_Fields.QUANTITY:
                    case Order_Offer_Warning_Type_Fields.QUANTITY_MAX:
                    case Order_Offer_Warning_Type_Fields.QUANTITY_MIN:
                      props.align = 'right';
                      props.headerAlign = 'right';
                      props.width = 100;
                      break;
                    case Order_Offer_Warning_Type_Fields.AMOUNT_AFTER_SPLITTING:
                      props.align = 'right';
                      props.headerAlign = 'right';
                      props.width = 200;
                      break;
                  }

                  return (
                    <Column
                      headerName={t(`enums.${field}`)}
                      {...props}
                      sortable={false}
                      key={idx}
                    />
                  );
                })}
              </Table>
            );
          }
        }
      }
      return undefined;
    },
    [t],
  );

  useEffect(() => {
    const loadingSub = orderDetailsState.sub.saveLoading().subscribe((isLoading) => {
      setLoading(isLoading);
      if (isLoading) {
        snackbarService.pub.show({
          id: 'orderSaveOrderLoading',
          type: 'loading',
          content: t('order.save_order'),
          noAutoHide: true,
        });
      } else {
        snackbarService.pub.hide('orderSaveOrderLoading');
      }
    });

    const warningSub = orderDetailsState.sub.warnings().subscribe(({ warnings, linkedRecords }) => {
      if (warnings !== C_Order_Error_Code.OEC7_TOUR_IS_EMPTY_BUT_NEEDED) {
        setIsEmptyTourError(false);
      }
      if (warnings) {
        const warningTableComp = generateWarningTable(linkedRecords);
        if (warnings === C_Order_Error_Code.OEC7_TOUR_IS_EMPTY_BUT_NEEDED) {
          setIsEmptyTourError(true);
          snackbarService.pub.show({
            type: 'error',
            content: t('order.tour_is_empty_but_needed'),
          });
        }
        if (warnings === C_Order_Error_Code.OEC6_NOT_ENOUGH_DAYS_TO_PRODUCE) {
          const content = (
            <>
              <Typography sx={{ pb: 2 }}>{t('order.not_enough_days_to_produce')}</Typography>
              {warningTableComp}
            </>
          );
          confirmCreationModal.stream.emit('open', {
            title: t('order.edit_order'),
            content,
            width: 700,
            onConfirm: () => {
              orderDetailsState.pub.saveOrder({
                ignoreDaysToProduce: true,
                ignoreQuantityPerLotProblems: false,
                ignoreMinMaxProblems: false,
                ignoreSplittingProblems: false,
              });
            },
          });
        }
        if (warnings === C_Order_Error_Code.OEC3_QUANTITY_PER_LOT) {
          const content = (
            <>
              <Typography sx={{ pb: 2 }}>{t('order.positions_lot_quantity_warning')}</Typography>
              {warningTableComp}
            </>
          );
          confirmCreationModal.stream.emit('open', {
            title: t('order.edit_order'),
            content,
            width: 700,
            onConfirm: () => {
              orderDetailsState.pub.saveOrder({
                ignoreDaysToProduce: true,
                ignoreQuantityPerLotProblems: true,
                ignoreMinMaxProblems: false,
                ignoreSplittingProblems: false,
              });
            },
          });
        }
        if (warnings === C_Order_Error_Code.OEC4_MIN_MAX) {
          const content = (
            <>
              <Typography sx={{ pb: 2 }}>
                {t('order.positions_min_max_quantity_warning')}
              </Typography>
              {warningTableComp}
            </>
          );
          confirmCreationModal.stream.emit('open', {
            title: t('order.edit_order'),
            content,
            width: 700,
            onConfirm: () => {
              orderDetailsState.pub.saveOrder({
                ignoreDaysToProduce: true,
                ignoreQuantityPerLotProblems: true,
                ignoreMinMaxProblems: true,
                ignoreSplittingProblems: false,
              });
            },
          });
        }
        if (warnings === C_Order_Error_Code.OEC5_SPLITTING) {
          const content = (
            <>
              <Typography sx={{ pb: 2 }}>
                {t('order.delivery_splitting_calculations_warning')}
              </Typography>
              {warningTableComp}
            </>
          );
          confirmCreationModal.stream.emit('open', {
            title: t('order.edit_order'),
            content,
            width: 700,
            onConfirm: () => {
              orderDetailsState.pub.saveOrder({
                ignoreDaysToProduce: true,
                ignoreQuantityPerLotProblems: true,
                ignoreMinMaxProblems: true,
                ignoreSplittingProblems: true,
              });
            },
          });
        }
      }
    });

    return () => {
      loadingSub.unsubscribe();
      warningSub.unsubscribe();
      confirmCreationModal.stream.unsubscribe();
    };
  }, []);

  /* Catches a click aimed at the Save button that never reached it. Mid-cell-edit
  the order is not dirty yet (cells publish on blur), so the button is disabled and
  `pointer-events: none`: the press lands here and only blurs the editor — which is
  what makes the order dirty, so by mouseup the button is enabled and `click` goes to
  the common ancestor, here again. The first click was spent committing the edit and
  saved nothing (WA-3820). Regression test is browser-tier; jsdom has no
  pointer-events hit testing. */
  const saveFromWrapper = (e: MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    // Portalled warnings popup: bubbles up the React tree but is not a DOM child.
    if (!e.currentTarget.contains(target)) return;
    // The button was enabled and handled this click itself.
    if (target.closest('button')) return;
    if (dirty && !loading) orderDetailsState.pub.saveOrder();
  };

  return (
    <Stack onClick={saveFromWrapper}>
      <Button
        onClick={() => orderDetailsState.pub.saveOrder()}
        disabled={!dirty || loading}
        variant='contained'
      >
        {t('common.save')}
      </Button>
      <confirmCreationModal.Component />
    </Stack>
  );
};

interface IOrderActions {
  dirty: boolean;
  setIsEmptyTourError: Dispatch<SetStateAction<boolean>>;
}
